import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GameShell from '../../shared/ui/GameShell';
import LoadingScreen from '../../shared/ui/LoadingScreen';
import ErrorScreen from '../../shared/ui/ErrorScreen';
import FinishedDaily from '../../shared/ui/FinishedDaily';
import { finish as recordOutcome, isFinished } from '../../shared/dailySession';
import { useDailySnapshot, usePersistedState } from '../../shared/useDailySession';
import DailyDoneOverlay from '../../shared/ui/DailyDoneOverlay';
import Keyboard from '../../shared/ui/Keyboard';
import { loadTodaysPuzzle, formatDateLabel } from '../../shared/daily';
import { isValidWordFull } from '../../shared/dictionary';
import {
  normalize,
  validPrefixes,
  type ChainLink,
  type NormalizedWordDominoes,
  type WordDominoesLevel,
} from './types';
import './wordDominoes.css';

export default function WordDominoesGame() {
  const [data, setData] = useState<{
    puzzle: NormalizedWordDominoes;
    dateKey: string;
    isToday: boolean;
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadTodaysPuzzle<WordDominoesLevel>('wordDominoes')
      .then((res) => {
        if (cancelled) return;
        if (!res) {
          setLoadError('No daily puzzle published for Word Dominoes yet.');
          return;
        }
        const puzzle = normalize(res.data);
        if (!puzzle) {
          setLoadError('Today\'s Word Dominoes puzzle is malformed.');
          return;
        }
        setData({ puzzle, dateKey: res.dateKey, isToday: res.isToday });
      })
      .catch((e) => !cancelled && setLoadError(String(e?.message ?? e)));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loadError) return <ErrorScreen message={loadError} gameId="wordDominoes" />;
  if (!data) {
    return (
      <GameShell gameId="wordDominoes">
        <LoadingScreen label="Fetching today's puzzle…" />
      </GameShell>
    );
  }

  // A daily is one attempt. Once the round has resolved, show the result
  // instead of mounting a fresh board.
  if (isFinished('wordDominoes', data.dateKey)) {
    return (
      <FinishedDaily gameId="wordDominoes" dateKey={data.dateKey} isToday={data.isToday} />
    );
  }

  return (
    <WordDominoesPlay
      key={data.dateKey}
      puzzle={data.puzzle}
      dateKey={data.dateKey}
      isToday={data.isToday}
    />
  );
}

/** BFS the shortest path from `start` to `target` through `accepted`, using the
 *  variable-overlap rule (last 2 or 3 letters of one word must match the next
 *  word's first 2 or 3 letters). Returns the *first hop* of that shortest path. */
function findShortestPathStep(
  start: string,
  target: string,
  accepted: Set<string>,
  used: Set<string>,
): string | null {
  if (start === target) return null;
  const queue: string[] = [start];
  const parents = new Map<string, string>();
  const visited = new Set<string>([start]);
  while (queue.length > 0) {
    const word = queue.shift()!;
    const suffixes: string[] = [];
    if (word.length >= 3) suffixes.push(word.slice(-3));
    if (word.length >= 2) suffixes.push(word.slice(-2));
    for (const next of accepted) {
      if (visited.has(next) || next.length < 3) continue;
      if (!suffixes.some((s) => next.startsWith(s))) continue;
      visited.add(next);
      parents.set(next, word);
      if (next === target) {
        let current = next;
        let firstStep = next;
        while (parents.get(current) !== undefined && parents.get(current) !== start) {
          firstStep = parents.get(current)!;
          current = firstStep;
        }
        if (!used.has(firstStep)) return firstStep;
        return null;
      }
      queue.push(next);
    }
  }
  return null;
}

function WordDominoesPlay({
  puzzle,
  dateKey,
  isToday,
}: {
  puzzle: NormalizedWordDominoes;
  dateKey: string;
  isToday: boolean;
}) {
  const { restored, save } = useDailySnapshot<{
    chain: ChainLink[];
    hintShown: boolean;
  }>('wordDominoes', dateKey);

  const [chain, setChain] = useState<ChainLink[]>(
    () => restored?.chain ?? [{ word: puzzle.startWord, overlapFromPrevious: '' }],
  );
  const [currentInput, setCurrentInput] = useState('');
  const [hintShown, setHintShown] = useState(restored?.hintShown ?? false);
  const [completed, setCompleted] = useState(false);

  usePersistedState(save, { chain, hintShown }, !completed);
  const [shake, setShake] = useState(false);
  const [toast, setToast] = useState<{ msg: string; style: 'info' | 'success' | 'error' } | null>(
    null,
  );
  const scrollerRef = useRef<HTMLDivElement>(null);

  const showToast = useCallback((msg: string, style: 'info' | 'success' | 'error' = 'info') => {
    setToast({ msg, style });
    setTimeout(() => setToast(null), 1600);
  }, []);

  const triggerShake = useCallback(() => {
    setShake(true);
    setTimeout(() => setShake(false), 450);
  }, []);

  useEffect(() => {
    if (completed && puzzle.mode === 'goal')
      recordOutcome('wordDominoes', dateKey, 'won', { subtitle: 'Chain complete.' });
  }, [completed, dateKey, puzzle.mode]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [chain.length]);

  const lastWord = chain[chain.length - 1]?.word ?? puzzle.startWord;
  const prefixes = useMemo(() => validPrefixes(lastWord), [lastWord]);
  const movesUsed = Math.max(0, chain.length - 1);
  const remainingMoves = puzzle.mode === 'goal' ? Math.max(0, puzzle.moveLimit - movesUsed) : Infinity;

  const submit = useCallback(async () => {
    if (completed) return;
    const candidate = currentInput.toUpperCase();
    if (candidate.length < 3) {
      triggerShake();
      showToast('Word must be ≥ 3 letters', 'error');
      return;
    }
    const usedPrefix = prefixes.find((p) => candidate.startsWith(p));
    if (!usedPrefix) {
      triggerShake();
      showToast(`Must start with ${prefixes.join(' or ')}`, 'error');
      return;
    }
    const acceptedHere = puzzle.acceptedWords.has(candidate);
    if (!acceptedHere) {
      const valid = await isValidWordFull(candidate);
      if (!valid) {
        triggerShake();
        showToast('Not in dictionary', 'error');
        return;
      }
    }
    const isWinningMove = puzzle.mode === 'goal' && candidate === puzzle.targetWord;
    if (!isWinningMove && chain.some((l) => l.word === candidate)) {
      triggerShake();
      showToast('Already used', 'error');
      return;
    }
    const link: ChainLink = { word: candidate, overlapFromPrevious: usedPrefix };
    const nextChain = [...chain, link];
    setChain(nextChain);
    setCurrentInput('');
    showToast(`+${candidate}`, 'success');
    if (puzzle.mode === 'goal' && candidate === puzzle.targetWord) {
      setCompleted(true);
    } else if (puzzle.mode === 'goal' && nextChain.length - 1 >= puzzle.moveLimit) {
      triggerShake();
      showToast('Out of moves', 'error');
    }
  }, [completed, currentInput, prefixes, puzzle, chain, triggerShake, showToast]);

  const useHint = useCallback(() => {
    if (completed || hintShown) return;
    const used = new Set(chain.map((l) => l.word));
    let hintWord: string | null = null;
    if (puzzle.mode === 'goal' && puzzle.targetWord) {
      hintWord = findShortestPathStep(lastWord, puzzle.targetWord, puzzle.acceptedWords, used);
    }
    if (!hintWord) {
      const candidates = [...puzzle.solverPath, ...puzzle.acceptedWords];
      hintWord =
        candidates.find(
          (w) =>
            w.length >= 3 &&
            !used.has(w) &&
            puzzle.acceptedWords.has(w) &&
            prefixes.some((p) => w.startsWith(p)),
        ) ?? null;
    }
    if (!hintWord) {
      showToast('No hint available', 'info');
      return;
    }
    const usedPrefix = prefixes.find((p) => hintWord!.startsWith(p)) ?? '';
    setHintShown(true);
    const nextChain = [...chain, { word: hintWord, overlapFromPrevious: usedPrefix }];
    setChain(nextChain);
    showToast(`Hint: ${hintWord}`, 'info');
    if (puzzle.mode === 'goal' && hintWord === puzzle.targetWord) setCompleted(true);
  }, [completed, hintShown, chain, lastWord, prefixes, puzzle, showToast]);

  const onLetter = useCallback(
    (letter: string) => {
      if (completed) return;
      setCurrentInput((prev) => (prev.length < 20 ? prev + letter : prev));
    },
    [completed],
  );
  const onDelete = useCallback(() => {
    if (completed) return;
    setCurrentInput((prev) => prev.slice(0, -1));
  }, [completed]);

  const submitEnabled =
    !completed &&
    currentInput.length >= 3 &&
    prefixes.some((p) => currentInput.toUpperCase().startsWith(p)) &&
    (puzzle.mode === 'endless' || remainingMoves > 0);

  return (
    <GameShell
      gameId="wordDominoes"
      subtitle={
        puzzle.mode === 'goal'
          ? `${formatDateLabel(dateKey, isToday)} • ${movesUsed}/${puzzle.moveLimit} moves`
          : `${formatDateLabel(dateKey, isToday)} • ${chain.length} words`
      }
    >
      <div className="wd-board">
        {puzzle.mode === 'goal' && (
          <div className="wd-target">
            <div className="wd-target-line">
              <span className="wd-target-label">Target:</span>
              <span className="wd-target-word">{puzzle.targetWord}</span>
            </div>
            <div className="wd-target-line wd-target-sub">
              <span className="wd-target-label">Start:</span>
              <span className="wd-target-word-sm">{puzzle.startWord}</span>
            </div>
          </div>
        )}

        <div className="wd-chain" ref={scrollerRef}>
          {chain.map((link, i) => {
            const isStart = i === 0;
            const isTarget = puzzle.mode === 'goal' && link.word === puzzle.targetWord;
            const tail = link.word.slice(link.overlapFromPrevious.length);
            return (
              <div key={i} className={'wd-link-wrap'}>
                {i > 0 && <div className="wd-connector" />}
                <div
                  className={
                    'wd-tile' +
                    (isStart ? ' is-start' : '') +
                    (isTarget ? ' is-target' : '')
                  }
                >
                  {link.overlapFromPrevious && (
                    <span className="wd-overlap">{link.overlapFromPrevious}</span>
                  )}
                  <span className="wd-tail">{tail || link.word}</span>
                </div>
              </div>
            );
          })}
        </div>

        {prefixes.length > 0 && !completed && (
          <div className="wd-prefix-hint">
            Use <strong>{prefixes.join(' or ')}</strong> from {lastWord}
          </div>
        )}

        <div className={'wd-input' + (shake ? ' is-shake' : '')}>
          <span className={currentInput ? 'wd-input-text' : 'wd-input-placeholder'}>
            {currentInput || 'Type a word…'}
          </span>
          <span className="wd-input-len">{currentInput.length}</span>
          <button
            className="wd-input-clear"
            onClick={() => setCurrentInput('')}
            disabled={!currentInput}
            aria-label="Clear"
          >
            ✕
          </button>
        </div>

        {toast && <div className={`wd-toast wd-toast-${toast.style}`}>{toast.msg}</div>}

        <Keyboard
          onLetter={onLetter}
          onDelete={onDelete}
          onSubmit={submit}
          submitEnabled={submitEnabled}
          onHint={useHint}
          hintEnabled={!hintShown && !completed}
        />

        {completed && (
          <DailyDoneOverlay
            dateKey={dateKey}
            isToday={isToday}
            subtitle={
              puzzle.mode === 'goal'
                ? `Reached ${puzzle.targetWord} in ${movesUsed} moves (par ${puzzle.parChainLength}).`
                : `Built a chain of ${chain.length} words.`
            }
          />
        )}
      </div>
    </GameShell>
  );
}
